import type {
  AcademyCategory,
  CatalogQuery,
} from "@nyntax/contracts/schemas/course";

/** Same slugs as `ACADEMY_CATEGORIES` in `@nyntax/contracts`. */
export const ACADEMY_CATEGORIES = [
  "academics",
  "exam-prep",
  "language",
  "technology",
  "business",
  "design",
  "personal-development",
  "other",
] as const;

const MAX_SEARCH_LENGTH = 100;

const CATEGORY_SET: ReadonlySet<string> = new Set(ACADEMY_CATEGORIES);

const isAcademyCategory = (value: string): value is AcademyCategory =>
  CATEGORY_SET.has(value);

const firstValue = (
  value: string | string[] | undefined
): string | undefined => (Array.isArray(value) ? value[0] : value);

/** Raw `q` and `category` values from the page's search params. */
export interface CatalogSearchInput {
  category?: string | string[];
  q?: string | string[];
}

/**
 * Reads `?q=&category=` from the Catalog page. Drops a blank search, trims
 * to 100 characters, and ignores a category that is not in the Academy set.
 *
 * @param input - The raw search params from the page.
 * @returns The query the Catalog API and the filter links should share.
 */
export const readCatalogQuery = (input: CatalogSearchInput): CatalogQuery => {
  const q = (firstValue(input.q) ?? "").trim().slice(0, MAX_SEARCH_LENGTH);
  const categoryValue = (firstValue(input.category) ?? "").trim();
  const category = isAcademyCategory(categoryValue) ? categoryValue : undefined;

  return {
    ...(q.length > 0 ? { q } : {}),
    ...(category === undefined ? {} : { category }),
  };
};

/** Extra Catalog view options carried in the address besides the query. */
export interface CatalogViewOptions {
  /** Only free courses (`?price=free`). */
  freeOnly?: boolean;
  /** The sort, left out of the address when it is the default. */
  sort?: string;
}

/**
 * The Catalog URL for a locale, including the search, category, sort and
 * free filter when set.
 *
 * @param locale - The active locale segment.
 * @param query - The normalized Catalog query.
 * @param view - The sort and free filter to keep.
 * @returns A path such as `/en-US` or `/en-US?q=algebra&category=academics`.
 */
export const catalogPath = (
  locale: string,
  query: CatalogQuery,
  view: CatalogViewOptions = {}
): string => {
  const params = new URLSearchParams();

  if (query.q !== undefined && query.q !== "") {
    params.set("q", query.q);
  }

  if (query.category !== undefined) {
    params.set("category", query.category);
  }

  if (view.sort !== undefined && view.sort !== "popular") {
    params.set("sort", view.sort);
  }

  if (view.freeOnly === true) {
    params.set("price", "free");
  }

  const search = params.toString();

  return search === "" ? `/${locale}` : `/${locale}?${search}`;
};
